/** @jsxImportSource @emotion/react */
import * as React from "react";
import * as emailTemplateActions from "./emailTemplateActions";
import * as authSelectors from "../auth/authSelectors";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { EmailTemplate } from "./models/EmailTemplate";
import { OrganizationKeyName } from "../../model/Organization";
import { EmailAttachmentUrls } from "./models/EmailTemplatesSuccessResponse";
import { FormProvider, useForm } from "react-hook-form";
import { TextInput } from "../../components/forms/inputs/TextInput";
import { TextAreaInput } from "../../components/forms/inputs/TextAreaInput";
import { css } from "@emotion/react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextAreaField } from "../../components/forms/fields/TextAreaField";
import { FormRow } from "../common/hook-form";

type Props = {
  emailTemplate: EmailTemplate | Partial<EmailTemplate>;
  onSubmit: (values: EmailTemplate) => Promise<void>;
  isUpsertingEmailTemplate: boolean;
};

const inputGap = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
`;

const validationSchema = yup.object().shape({
  templateName: yup.string().required("Please enter template name to continue"),
  replyToAddress: yup
    .string()
    .required("Please enter reply to address to continue"),
  replyToName: yup.string().required("Please enter reply to name to continue"),
  fromAddress: yup.string().required("Please enter from address to continue"),
  fromName: yup.string().required("Please enter from name to continue"),
  subject: yup.string().required("Please enter subject to continue"),
  htmlText: yup.string().required("Please enter HTML code to continue"),
  plainText: yup.string().required("Please enter plain text to continue"),
  locale: yup.string().required("Please enter locale to continue"),
});

export const EmailTemplateUpsertForm = ({
  emailTemplate,
  onSubmit,
  isUpsertingEmailTemplate,
}: Props) => {
  const dispatch = useDispatch();
  const organization = useSelector(authSelectors.selectedOrganizationKeyName);
  const { t } = useTranslation();
  const [attachmentUrls, setAttachmentUrls] = React.useState<
    EmailAttachmentUrls[]
  >([]);
  const [placeholders, setPlaceholders] = React.useState<string>("");
  const methods = useForm<EmailTemplate>({
    defaultValues: {
      ...emailTemplate,
    },
    /* @ts-ignore */
    resolver: yupResolver(validationSchema),
  });

  React.useEffect(() => {
    if (!emailTemplate) {
      return;
    }
    if (emailTemplate.attachmentUrls) {
      setAttachmentUrls(emailTemplate.attachmentUrls);
    }
  }, [emailTemplate]);

  const sendTestEmailTemplate = (
    data: any,
    organization: OrganizationKeyName
  ) => {
    dispatch(
      emailTemplateActions.sendTestEmailTemplateAction(organization, data)
    );
  };

  const handleSubmit = async (values: EmailTemplate) => {
    const attachments = (emailTemplate.attachmentUrls || [])
      .filter((el) => !!el.url)
      .map((at) => ({ url: at.url, contentType: at.contentType }));

    const v = {
      ...values,
      attachmentUrls: attachments,
    };

    await onSubmit(v);
  };

  const onSendTest = (values: any) => {
    const attachments = attachmentUrls
      .filter((el) => !!el.url)
      .map((at) => ({ url: at.url, contentType: at.contentType }));
    let jsonPlaceholders;
    try {
      jsonPlaceholders = JSON.parse(placeholders);
      const textBody = values.plainText;
      const htmlBody = values.htmlText;

      const { plainText, htmlText, ...rest } = values;

      const v = {
        ...rest,
        textBody,
        htmlBody,
        placeholders: jsonPlaceholders,
        attachmentUrls: attachments,
      };

      sendTestEmailTemplate(v, organization);
    } catch (error) {
      alert(
        'Invalid JSON format. Format the input this way: [{"placeholder1": "value1"}, {"placeholder2": "value2"}]'
      );
    }
  };

  const onChangeAttachmentUrls = (index: number) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.currentTarget;

    const attachments = [...attachmentUrls];

    attachments[index] = {
      url: value,
      contentType: "application/pdf",
    };
    setAttachmentUrls(attachments);
  };

  return (
    /* @ts-ignore */
    <FormProvider {...methods}>
      <form className="card">
        <div className="card-header">
          <h3 className="card-title">
            {emailTemplate.id ? (
              <>Email template edit</>
            ) : (
              <>New email template</>
            )}
          </h3>
        </div>
        <div className="card-body">
          <div className="row">
            <div css={inputGap} className="col-12">
              <TextInput
                name="templateName"
                label="Template name"
                helperText="Name of email template"
              />
              <TextInput
                name="replyToAddress"
                label="Reply to address"
                helperText="Reply address for receivers of the email"
              />
              <TextInput
                name="replyToName"
                label="Reply to name"
                helperText="Reply name for receivers of the email"
              />
              <TextInput
                name="fromAddress"
                label="From address"
                helperText="The address of the sender"
              />
              <TextInput
                name="fromName"
                label="From name"
                helperText="The name of the sender"
              />
              <TextInput
                name="subject"
                label="Subject"
                helperText="Subject of email template"
              />
              <TextAreaInput
                name="htmlText"
                label="HTML text"
                helperText="The actual HTML content of the email"
              />
              <TextAreaInput name="plainText" label="Plain text" />
              <TextInput name="locale" label="Locale" />
            </div>
          </div>
          <div>
            <label className="form-label">
              Attachment urls
              <small className="form-text text-muted">Content type: PDF</small>
            </label>
            <div className="row">
              <div className="col-12 mb-1">
                <input
                  className="form-control"
                  value={attachmentUrls[0] ? attachmentUrls[0].url : ""}
                  onChange={onChangeAttachmentUrls(0)}
                />
              </div>
              <div className="col-12 mb-1">
                <input
                  className="form-control"
                  value={attachmentUrls[1] ? attachmentUrls[1].url : ""}
                  onChange={onChangeAttachmentUrls(1)}
                />
              </div>
              <div className="col-12 mb-1">
                <input
                  className="form-control"
                  value={attachmentUrls[2] ? attachmentUrls[2].url : ""}
                  onChange={onChangeAttachmentUrls(2)}
                />
              </div>
            </div>
          </div>
          <FormRow>
            <TextAreaField
              onChange={(e) => setPlaceholders(e.target.value)}
              label="JSON placeholders"
              helperText='Enter stringified JSON object with placeholders for sending test email. Example: [{"placeholder1": "value1"}, {"placeholder2": "value2"}]'
            />
          </FormRow>
        </div>
        <div className="card-footer text-right">
          <button
            className="btn mr-3"
            type="button"
            onClick={() => onSendTest(emailTemplate)}
          >
            Send test
          </button>
          <button
            className="btn btn-primary"
            type="button"
            disabled={isUpsertingEmailTemplate}
            onClick={methods.handleSubmit(handleSubmit)}
          >
            {isUpsertingEmailTemplate ? (
              <>
                {t("email_template_upsert_form.submit_button.label_is_saving")}
              </>
            ) : (
              <>{t("email_template_upsert_form.submit_button.label")}</>
            )}
          </button>
        </div>
      </form>
    </FormProvider>
  );
};
